import React, { useState } from 'react';
import { FiPlay, FiCheckCircle, FiLock, FiClock, FiStar } from 'react-icons/fi';
import { useAppContext } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';

export const Academy = () => {
  const { modules, completeModule } = useAppContext();
  const [activeModule, setActiveModule] = useState(null);

  const handleComplete = (id) => {
    completeModule(id);
    setActiveModule(null);
  };

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Training Academy</h1>
        <p className="text-slate-400">Master your craft with bite-sized field modules.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {modules.map((mod, idx) => (
          <motion.div 
            key={mod.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            onClick={() => setActiveModule(mod)}
            className={`glass-panel p-5 rounded-2xl cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-900/20 ${
              mod.completed ? 'border-emerald-500/30 bg-slate-800/40' : 'hover:border-blue-500/50'
            }`}
          >
            <div className="flex justify-between items-start mb-3">
              <div className={`p-2 rounded-lg ${mod.completed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>
                {mod.completed ? <FiCheckCircle className="text-xl" /> : <FiPlay className="text-xl ml-0.5" />}
              </div>
              <div className="flex items-center gap-1 text-xs font-medium text-amber-400 bg-amber-400/10 px-2 py-1 rounded-full">
                <FiStar /> +{mod.xp} XP
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-white mb-1">{mod.title}</h3>
            <p className="text-sm text-slate-400 line-clamp-2 mb-4">{mod.summary}</p>
            
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <FiClock /> {mod.duration} • {mod.track.toUpperCase()} Track
            </div>
          </motion.div>
        ))}
      </div>

      {/* Mock Lesson Modal */}
      <AnimatePresence>
        {activeModule && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              {/* Dummy Video Area */}
              <div className="w-full aspect-video bg-black relative group flex items-center justify-center">
                <img src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=600&auto=format&fit=crop" className="absolute inset-0 w-full h-full object-cover opacity-50" alt="lesson" />
                <button className="relative z-10 w-16 h-16 rounded-full bg-blue-600/90 text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg shadow-blue-900/50">
                  <FiPlay className="text-2xl ml-1" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-bold text-white">{activeModule.title}</h2>
                  <span className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded-md">{activeModule.duration}</span>
                </div>
                
                <h4 className="text-sm font-semibold text-slate-300 mb-2">Key Takeaways:</h4>
                <ul className="list-disc list-inside text-sm text-slate-400 space-y-2 mb-6 ml-1">
                  <li>Always inspect visually before cleaning.</li>
                  <li>Use dry cleaning methods first.</li>
                  <li>Never touch the ferrule end-face.</li>
                </ul>

                <div className="flex gap-3 mt-auto pt-4 border-t border-slate-800">
                  <button onClick={() => setActiveModule(null)} className="btn-secondary flex-1">Close</button>
                  <button onClick={() => handleComplete(activeModule.id)} disabled={activeModule.completed} className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${activeModule.completed ? 'bg-emerald-600/50 text-emerald-200 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                    {activeModule.completed ? 'Completed' : 'Finish & Claim XP'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};